import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { setApiErrorNotifier } from './lib/httpClient';

// Pages
import LandingPage from './pages/LandingPage';
import { LoginPage, AuthCallbackPage } from './pages/AuthPages';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordResetPages';
import DashboardPage from './pages/DashboardPage';
import { CallLogsPage, CallDetailPage } from './pages/CallsPages';
import { MessageLogsPage, MessageDetailPage } from './pages/MessagesPages';
import { PricingPage, PaymentSuccessPage } from './pages/PricingPages';
import { PaymentOrderDetailsPage, PaymentHistoryPage } from './pages/PaymentPages';
import ProfilePage from './pages/ProfilePage';
import AlertSettingsPage from './pages/AlertSettingsPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import JobDetailPage from './pages/JobDetailPage';
import { AgentsPage, AgentDetailPage } from './pages/AgentsPages';
import { SupportTicketsPage, SupportNewTicketPage, SupportTicketDetailPage } from './pages/SupportPages';
import AgentCalendarPage from './pages/AgentCalendarPage';
import MyAppointmentsPage from './pages/MyAppointmentsPage';
import PublicBookingPage from './pages/PublicBookingPage';
import LegalPage from './pages/LegalPages';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import { CustomerOnboardingPage, CustomerOnboardingSuccessPage } from './pages/CustomerOnboardingPages';
import MissedCallAuditPage from './pages/MissedCallAuditPage';
import { BlogIndexPage, BlogDetailPage } from './pages/BlogPages';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import { AdminCustomersPage, AdminCustomerDetailPage } from './pages/admin/AdminCustomersPage';
import { AdminAgentsPage, AdminAgentCreatePage, AdminAgentDetailPage } from './pages/admin/AdminAgentsPage';
import AdminAgentConfigurationPage from './pages/admin/AdminAgentConfigurationPage';
import { AdminAnalyticsPage, AdminRevenuePage } from './pages/admin/AdminAnalyticsPages';
import { AdminPlansPage, AdminPackagesPage } from './pages/admin/AdminManagementPages';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminAgentSubscriptionsPage from './pages/admin/AdminAgentSubscriptionsPage';
import {
  AdminSupportLayout,
  AdminSupportDashboardPage,
  AdminSupportTicketsListPage,
  AdminSupportTicketDetailPage,
} from './pages/admin/AdminSupportPages';

// Sales Pages
import SalesLoginPage from './pages/sales/SalesLoginPage';
import SalesDashboardPage from './pages/sales/SalesDashboardPage';
import LeadsListPage from './pages/sales/LeadsListPage';
import LeadDetailPage from './pages/sales/LeadDetailPage';
import AddLeadPage from './pages/sales/AddLeadPage';
import BulkUploadPage from './pages/sales/BulkUploadPage';

// Service Pages
import ServiceLoginPage from './pages/service/ServiceLoginPage';
import ServiceDashboardPage from './pages/service/ServiceDashboardPage';
import ServiceBusinessesPage from './pages/service/ServiceBusinessesPage';
import ServiceBusinessDetailPage from './pages/service/ServiceBusinessDetailPage';
import ServiceBusinessTwilioConfigPage from './pages/service/ServiceBusinessTwilioConfigPage';
import {
  ServiceAgentsPage,
  ServiceAgentCreatePage,
  ServiceAgentDetailPage,
} from './pages/service/ServiceAgentsPage';
import ServiceAgentConfigurationPage from './pages/service/ServiceAgentConfigurationPage';
import ServiceCallsPage from './pages/service/ServiceCallsPage';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';
import AdminLayout from './layouts/AdminLayout';
import SalesLayout from './layouts/SalesLayout';
import ServiceLayout from './layouts/ServiceLayout';

import './App.css';

// Auth callback detection wrapper
function AppRouter() {
  const location = useLocation();

  useEffect(() => {
    // SPA route tracking hook for GTM containers.
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'page_view',
        page_path: `${location.pathname}${location.search}${location.hash}`,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search, location.hash]);
  
  // Check URL fragment for session_id (OAuth callback)
  // This check happens synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/customer/onboarding" element={<CustomerOnboardingPage />} />
      <Route path="/customer/onboarding/success" element={<CustomerOnboardingSuccessPage />} />
      <Route path="/c/:publicToken" element={<PublicBookingPage />} />
      <Route path="/missed-call-audit-87k" element={<MissedCallAuditPage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogDetailPage />} />
      
      {/* Customer Dashboard Routes */}
      <Route element={<DashboardLayout />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/calls" element={<CallLogsPage />} />
        <Route path="/calls/:callId" element={<CallDetailPage />} />
        <Route path="/messages" element={<MessageLogsPage />} />
        <Route path="/messages/:messageId" element={<MessageDetailPage />} />
        <Route path="/buy-minutes" element={<PricingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/orders/:orderId" element={<PaymentOrderDetailsPage />} />
        <Route path="/payments/history" element={<PaymentHistoryPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/alerts" element={<AlertSettingsPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:customerId" element={<CustomerDetailPage />} />
        <Route path="/agents" element={<AgentsPage />} />
        <Route path="/agents/:agentId" element={<AgentDetailPage />} />
        <Route path="/appointments" element={<MyAppointmentsPage />} />
        <Route path="/agents/:agentId/calendar" element={<AgentCalendarPage />} />
        <Route path="/jobs/:jobId" element={<JobDetailPage />} />
        <Route path="/support" element={<SupportTicketsPage />} />
        <Route path="/support/new" element={<SupportNewTicketPage />} />
        <Route path="/support/:ticketId" element={<SupportTicketDetailPage />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="customers" element={<AdminCustomersPage />} />
        <Route path="customers/:userId" element={<AdminCustomerDetailPage />} />
        <Route path="agents" element={<AdminAgentsPage />} />
        <Route path="agents/new" element={<AdminAgentCreatePage />} />
        <Route path="agents/:agentId" element={<AdminAgentDetailPage />} />
        <Route path="agents/:agentId/configure" element={<AdminAgentConfigurationPage />} />
        <Route path="customers/:businessId/twilio" element={<ServiceBusinessTwilioConfigPage />} />
        <Route path="analytics" element={<AdminAnalyticsPage />} />
        <Route path="revenue" element={<AdminRevenuePage />} />
        <Route path="plans" element={<AdminPlansPage />} />
        <Route path="packages" element={<AdminPackagesPage />} />
        <Route path="agent-subscriptions" element={<AdminAgentSubscriptionsPage />} />
        <Route path="support" element={<AdminSupportLayout />}>
          <Route index element={<AdminSupportDashboardPage />} />
          <Route path="tickets" element={<AdminSupportTicketsListPage />} />
          <Route path="tickets/:ticketId" element={<AdminSupportTicketDetailPage />} />
        </Route>
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
        {/* Admin also has access to leads */}
        <Route path="leads" element={<LeadsListPage />} />
        <Route path="leads/new" element={<AddLeadPage />} />
        <Route path="leads/upload" element={<BulkUploadPage />} />
        <Route path="leads/:leadId" element={<LeadDetailPage />} />
      </Route>

      {/* Sales Routes */}
      <Route path="/sales/login" element={<SalesLoginPage />} />
      <Route path="/sales" element={<SalesLayout />}>
        <Route index element={<SalesDashboardPage />} />
        <Route path="leads" element={<LeadsListPage />} />
        <Route path="leads/new" element={<AddLeadPage />} />
        <Route path="leads/upload" element={<BulkUploadPage />} />
        <Route path="leads/:leadId" element={<LeadDetailPage />} />
      </Route>

      {/* Service Routes */}
      <Route path="/service/login" element={<ServiceLoginPage />} />
      <Route path="/service" element={<ServiceLayout />}>
        <Route index element={<ServiceDashboardPage />} />
        <Route path="businesses" element={<ServiceBusinessesPage />} />
        <Route path="businesses/:businessId" element={<ServiceBusinessDetailPage />} />
        <Route path="businesses/:businessId/twilio" element={<ServiceBusinessTwilioConfigPage />} />
        <Route path="agents" element={<ServiceAgentsPage />} />
        <Route path="agents/new" element={<ServiceAgentCreatePage />} />
        <Route path="agents/:agentId" element={<ServiceAgentDetailPage />} />
        <Route path="agents/:agentId/configure" element={<ServiceAgentConfigurationPage />} />
        <Route path="calls" element={<ServiceCallsPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ApiErrorToastBridge />
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

function ApiErrorToastBridge() {
  useEffect(() => {
    setApiErrorNotifier((normalizedError) => {
      toast.error(normalizedError.uiMessage);
    });

    return () => {
      setApiErrorNotifier(null);
    };
  }, []);

  return null;
}

export default App;
